import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { Reveal, LineReveal, Overline } from "@/components/Primitives";
import { SERVICES, CHAPTERS, STATS, IMAGES } from "@/data";

const EASE = [0.16, 1, 0.3, 1];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden bg-obsidian text-bone">
      <div className="mx-auto grid min-h-screen max-w-[1400px] grid-cols-1 items-end gap-10 px-6 pb-16 pt-36 lg:grid-cols-12 lg:px-12 lg:pb-24">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}>
            <Overline dark>Bridge Advisors LLP · Est. 1991</Overline>
          </motion.div>

          <h1 className="mt-8 font-serif text-[13vw] font-normal leading-[0.92] tracking-tight sm:text-[10vw] lg:text-[6.4vw]">
            <LineReveal
              delay={0.35}
              lines={[
                "Financial clarity",
                <>for <span className="italic text-bone/70">complex</span></>,
                "operations.",
              ]}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9, ease: EASE }}
            className="mt-10 max-w-xl text-base leading-relaxed text-bone/70 md:text-lg"
          >
            Accounting, bookkeeping, CFO advisory, cost optimization and standards
            implementation — built for US organizations that demand disciplined
            financial leadership.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.9, ease: EASE }}
            className="mt-12 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-consult-btn"
              className="group inline-flex items-center gap-2 bg-bone px-8 py-4 text-sm font-medium text-obsidian transition-colors duration-300 hover:bg-brand hover:text-bone"
            >
              Schedule a Consultation
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-services-btn"
              className="inline-flex items-center gap-2 border border-bone/30 px-8 py-4 text-sm font-medium text-bone transition-colors duration-300 hover:bg-bone/10"
            >
              Explore Services
            </Link>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1.4, ease: EASE }}
            className="relative aspect-[3/4] w-full overflow-hidden"
          >
            <motion.img
              src={IMAGES.hero}
              alt="Finance professionals reviewing reports"
              style={{ y: imgY }}
              className="img-editorial absolute inset-0 h-[118%] w-full object-cover"
            />
            <div className="absolute inset-0 border border-bone/10" />
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="pointer-events-none absolute bottom-8 left-1/2 hidden -translate-x-1/2 items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-bone/40 lg:flex"
      >
        <ArrowDown size={12} className="animate-bounce" /> Scroll
      </motion.div>
    </section>
  );
}

function Ribbon() {
  const items = ["US GAAP Compliant", "Senior-Level Advisory", "Remote Secure Operations", "US Time-Zone Availability"];
  return (
    <div data-testid="marquee-ribbon" className="border-y border-border bg-sage py-5">
      <Marquee speed={40} gradient={false}>
        {items.concat(items).map((t, i) => (
          <span key={i} className="mx-8 inline-flex items-center gap-8 font-serif text-2xl italic text-obsidian md:text-3xl">
            {t}
            <span className="text-brand">✦</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
}

function Manifesto() {
  return (
    <section data-testid="manifesto-section" className="mx-auto max-w-[1400px] px-6 py-24 lg:px-12 lg:py-36">
      <div className="grid grid-cols-1 gap-16 md:grid-cols-12">
        <div className="md:col-span-4">
          <Reveal>
            <Overline>Why Bridge Advisors</Overline>
            <h2 className="mt-8 font-serif text-4xl leading-tight tracking-tight text-obsidian md:text-5xl">
              Trusted guidance, a <span className="italic">practical</span> approach.
            </h2>
            <p className="mt-6 max-w-sm text-stone">
              We reduce complexity, strengthen controls, and give leadership teams
              confidence in the numbers.
            </p>
          </Reveal>
        </div>

        <div className="md:col-span-8 md:pl-8">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.num} delay={i * 0.08}>
              <div className="grid grid-cols-1 gap-6 border-t border-border py-10 sm:grid-cols-12 sm:gap-8">
                <span className="font-mono text-sm text-brand sm:col-span-2">[ {c.num} ]</span>
                <h3 className="font-serif text-2xl text-obsidian sm:col-span-4">{c.title}</h3>
                <p className="text-stone sm:col-span-6">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Stats() {
  return (
    <section data-testid="stats-section" className="bg-obsidian text-bone">
      <div className="mx-auto grid max-w-[1400px] grid-cols-2 gap-px bg-bone/10 lg:grid-cols-4">
        {STATS.map((s, i) => (
          <Reveal key={s.value} delay={i * 0.08} className="bg-obsidian">
            <div className="px-6 py-14 lg:px-10 lg:py-20">
              <p className="font-serif text-3xl tracking-tight md:text-4xl">{s.value}</p>
              <p className="mt-3 font-mono text-xs uppercase tracking-[0.15em] text-bone/50">{s.label}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function ServicesPreview() {
  return (
    <section data-testid="services-preview" className="mx-auto max-w-[1400px] px-6 py-24 lg:px-12 lg:py-36">
      <Reveal className="mb-16 flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div>
          <Overline>Core Services</Overline>
          <h2 className="mt-8 max-w-2xl font-serif text-4xl leading-tight tracking-tight text-obsidian md:text-5xl">
            Advisory support built for <span className="italic">finance leaders.</span>
          </h2>
        </div>
        <Link to="/services" data-testid="services-preview-all" className="link-underline whitespace-nowrap text-sm font-medium text-brand">
          All services →
        </Link>
      </Reveal>

      <div className="grid grid-cols-1 border-l border-t border-border sm:grid-cols-2 lg:grid-cols-3">
        {SERVICES.map((s, i) => (
          <Reveal key={s.id} delay={(i % 3) * 0.07}>
            <Link
              to="/services"
              data-testid={`service-card-${s.id}`}
              className="group flex h-full flex-col justify-between border-b border-r border-border p-8 transition-colors duration-500 hover:bg-obsidian hover:text-bone lg:p-10"
            >
              <div className="flex items-start justify-between">
                <span className="font-mono text-xs text-brand transition-colors group-hover:text-bone/60">[ {s.num} ]</span>
                <ArrowUpRight size={18} className="text-stone transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-bone" />
              </div>
              <div className="mt-24">
                <h3 className="font-serif text-2xl text-obsidian transition-colors group-hover:text-bone">{s.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-stone transition-colors group-hover:text-bone/70">{s.short}</p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function ParallaxDivider() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section ref={ref} data-testid="parallax-divider" className="relative h-[60vh] overflow-hidden bg-obsidian">
      <motion.img
        src={IMAGES.architecture}
        alt="Minimalist architecture"
        style={{ y }}
        className="absolute inset-0 h-[130%] w-full object-cover opacity-60"
      />
      <div className="absolute inset-0 flex items-center justify-center px-6">
        <Reveal>
          <p className="max-w-3xl text-center font-serif text-3xl leading-tight tracking-tight text-bone md:text-5xl">
            Confidence built on <span className="italic text-bone/70">results.</span>
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function ClosingCTA() {
  return (
    <section data-testid="home-cta" className="mx-auto max-w-[1400px] px-6 py-24 lg:px-12 lg:py-36">
      <Reveal className="border border-border bg-sage p-10 md:p-20">
        <div className="grid grid-cols-1 items-center gap-10 md:grid-cols-12">
          <div className="md:col-span-8">
            <Overline>Start the conversation</Overline>
            <h2 className="mt-8 font-serif text-4xl leading-tight tracking-tight text-obsidian md:text-5xl">
              Tell us about your accounting, reporting or advisory needs.
            </h2>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link
              to="/contact"
              data-testid="cta-book-btn"
              className="group inline-flex items-center gap-2 bg-obsidian px-8 py-4 text-sm font-medium text-bone transition-colors duration-300 hover:bg-brand"
            >
              Book a Consultation
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <Ribbon />
      <Manifesto />
      <Stats />
      <ServicesPreview />
      <ParallaxDivider />
      <ClosingCTA />
    </>
  );
}
